import {
    AspectType,
    ColorDefinition,
    ColorOption,
    CommandWithFollowup,
    FontData,
    ILayout,
    LayoutElementType,
    LayoutsState,
    OptionResource,
    Region,
    RegionElement,
    Step,
    StepService,
    TextboxElement,
    TextFillImage,
    TextStepData,
    TextStepStorage,
    VariantResource,
} from "../../../types";
import { InformationMessageType, WorkflowManager } from "../../../WorkflowManager";
import { optionService } from "../../option";
import { toast } from "../../toast";
import { AssetNotFoundError, ResourceNotFoundError, UnhandledBehaviorError } from "../../../util/exception";
import { split } from "unicode-default-word-boundary";
import {
    CanvasCommand,
    CreateElementCommand,
    FontAlignmentCommand,
    FontColorCommand,
    FontImageFillCommand,
    FontSizeCommand,
    FontSourceCommand,
    GroupCommand,
    TextChangeCommand,
    TextStrokeCommand,
} from "../../../command";
import { getPatternImageData } from "../../../util/frame";
import { findElement } from "../../../LayoutsState";
import { getFontMetrics, loadFont } from "../../../util/font";
import { applyTextTransformations } from "../../../text/shared";
import { determineCorrectFontSizeAndLines } from "../../../text/algorithm/autosize";
import { generate } from "../../../util/guid";
import { renderTextTemplateForWorkflow } from "../../../util/text";

/**
 * The font size used when the font size is not specified in configuration.
 */
const defaultFontSize = 30;
interface TextFillSpotColor {
    profileName: string;
    namedColor: string;
}
interface CreatedElementData {
    regionElement: RegionElement;
    commands: CanvasCommand[];
    newElement: TextboxElement;
    fontData: FontData;
}

interface TextUpdateResult {
    command?: GroupCommand;
    helperData: {
        charactersRemaining?: number;
    };
    errorData?: TextErrorData;
}

export interface TextErrorData {
    blockedProfanity?: boolean;
    doesNotFit?: boolean;
    hitCharacterLimit?: boolean;
    illegalMultipleLines?: boolean;
    unsupportedCharacters?: boolean;
}

// https://joefallon.net/2018/09/typescript-try-catch-finally-and-custom-errors/
class FontLoadError extends Error {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = FontLoadError.name;
    }
}

// A set of allowable glyphs, open type doesn't see these glyphs as valid
// but they're generally always actually supplied. To prevent filtering them out
// we check if a glyph matches this list.
const allowableGlyphs = ["‘", "’", "“", "”", "\n"];

class RegionLayoutMissingError extends Error {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = RegionLayoutMissingError.name;
    }
}

class TextStepService implements StepService<TextStepData> {
    // A cache of computed possible colors. We store here to get a stable reference
    // to prevent react from re-rendering
    private cachedColors = new Map<string, ColorOption[]>();
    // HACK: These two could possibly be merged
    private cachedStrokeColors = new Map<string, ColorOption[]>();

    public async init(
        stepData: Step<TextStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) {
            return null;
        }
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            const defaultImageFillVariant = this.getDefaultImageFillVariant(stepData.data);
            const imageFillLink = defaultImageFillVariant?.asset?.fileLink;
            const imageFillDataRaw = imageFillLink ? await getPatternImageData(imageFillLink) : undefined;
            const imageFillData = imageFillDataRaw
                ? {
                      src: imageFillDataRaw.src,
                      height: imageFillDataRaw.height,
                      width: imageFillDataRaw.width,
                      scale: stepData.data.imageFillScale || 1,
                  }
                : undefined;
            const defaultStroke = this.getDefaultStrokeColorVariant(stepData.data);
            const strokeData =
                stepData.data.strokeEnabled && defaultStroke?.color
                    ? {
                          browserValue: defaultStroke.color,
                          pmsValue: defaultStroke.name,
                      }
                    : undefined;

            let text = stepData.data.defaultText || "";
            // When a bundle is present we load the text aspect.
            const bundle = workflowManager.getWorkflowExperience().getBundle();
            if (bundle && stepData.globalPropertyAspectConfigurations) {
                const aspects = bundle.getGlobalPropertyConfiguration()?.aspects;
                const aspect = aspects?.find(
                    (a) =>
                        a.type === AspectType.Text &&
                        stepData.globalPropertyAspectConfigurations?.map((c) => c.aspectName).includes(a.name),
                );
                if (aspect) {
                    const stateManager = bundle.getGlobalPropertyStateManager();
                    const aspectText = stateManager.getAspect(aspect.name);
                    if (aspectText) {
                        text = aspectText;
                    }
                }
            }

            workflowManager.updateStorage(stepData.stepName, {
                text,
                inputText: stepData.data.defaultText || "",
                color: this.getDefaultColorVariant(stepData.data)?.color || this.getDefaultColor(stepData.data),
                fillImage: imageFillData,
                strokeColor: strokeData,
            });
            workflowManager.updateMetadata(stepData.stepName, {
                text,
                color: this.getDefaultColorVariant(stepData.data)?.name || this.getDefaultColor(stepData.data),
                fillImage: defaultImageFillVariant?.name,
                strokeColor: stepData.data.strokeEnabled ? defaultStroke?.color : undefined,
            });

            const defaultVariant = optionService.getDefaultVariant(option);
            if (defaultVariant) {
                return this.selectVariantCommand(
                    stepData,
                    defaultVariant,
                    { text },
                    [],
                    workflowManager,
                    () => {},
                    () => {},
                    imageFillData,
                    strokeData,
                );
            }
        }
        return null;
    }

    public findLayoutElements(workflowManager: WorkflowManager, step: Step<TextStepData>) {
        const elements = workflowManager.getRegionElements(step.stepName);
        const foundElements = elements.map((el) => {
            return findElement(el.id, workflowManager.getAllLayoutData()) as TextboxElement;
        });
        return foundElements;
    }

    /**
     * Get the colors that can be used for a given text step.
     * @param stepData The text step to get colors for.
     * @returns A list of color objects containing fill, stroke and variant if available.
     */
    public availableFillColors(stepData: Step<TextStepData>): ColorOption[] {
        // Check cache first.
        const cached = this.cachedColors.get(stepData.stepName);
        if (cached) {
            return cached;
        }

        // Otherwise check option
        const colorOption = stepData.data.colorOption;
        if (colorOption) {
            const colors =
                colorOption.variants?.map((variant: VariantResource) => {
                    return {
                        fill: variant.color,
                        stroke: variant.color,
                        variant,
                    };
                }) || [];
            this.cachedColors.set(stepData.stepName, colors);
            return colors;
        }

        return [];
    }

    public changeAlignment(
        stepData: Step<TextStepData>,
        alignment: "left" | "center" | "right",
        elements: RegionElement[],
        workflowManager: WorkflowManager,
    ) {
        if (!stepData.data || !stepData.data.regions) {
            return;
        }
        const commandDispatcher = workflowManager.getCommandDispatcher();

        for (const regionElement of elements) {
            const command = new FontAlignmentCommand(regionElement.id, alignment);
            commandDispatcher(command);
        }
    }

    public async changeFillColor(
        stepData: Step<TextStepData>,
        newColor: ColorOption,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
    ) {
        if (!stepData.data || !stepData.data.regions) {
            return;
        }
        const commandDispatcher = workflowManager.getCommandDispatcher();

        if (newColor.variant) {
            workflowManager.updateMetadata(stepData.stepName, { color: newColor.variant.name });
        } else {
            workflowManager.updateMetadata(stepData.stepName, { color: newColor.fill });
        }
        // Handle generation of spot color information if required.
        const colorOption = stepData.data.colorOption;
        const textFillSpotColor = colorOption
            ? this.createTextFillSpotColor(colorOption, newColor.variant!)
            : undefined;
        if (colorOption) {
            workflowManager.updateStorage(stepData.stepName, {
                color: newColor.fill,
                colorProfileAssetKey: colorOption.colorProfile?.key,
            });
        } else {
            workflowManager.updateStorage(stepData.stepName, {
                color: newColor.fill,
            });
        }
        // Apply color changes to each element targeted by the step.
        for (const regionElement of elements) {
            if (!newColor.fill) {
                throw new Error("Fill not set on new color selection!");
            }
            const command = new FontColorCommand(regionElement.id, newColor.fill, textFillSpotColor);
            commandDispatcher(command);
        }
    }

    public async availableFillImages(stepData: Step<TextStepData>): Promise<TextFillImage[]> {
        const imageFillOption = stepData.data.imageFillOption;
        if (imageFillOption) {
            const imageFillUrls =
                (imageFillOption.variants?.map((v) => v.asset?.fileLink)?.filter((url) => !!url) as string[]) || [];
            return Promise.all(
                imageFillUrls.map(async (url) => {
                    const data = await getPatternImageData(url);
                    return {
                        src: url,
                        width: data.width,
                        height: data.height,
                        scale: stepData.data.imageFillScale || 1,
                    };
                }),
            );
        }
        return Promise.resolve([]);
    }

    public async changeFillImage(
        stepData: Step<TextStepData>,
        fillImage: TextFillImage,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
    ) {
        if (!stepData.data || !stepData.data.regions) {
            return;
        }
        const commandDispatcher = workflowManager.getCommandDispatcher();
        const variant = stepData.option?.variants?.find((v) => v.asset?.fileLink === fillImage.src);
        workflowManager.updateMetadata(stepData.stepName, { fillImage: variant?.name });
        workflowManager.updateStorage(stepData.stepName, {
            fillImage,
        });

        for (const regionElement of elements) {
            const command = new FontImageFillCommand(regionElement.id, fillImage);
            commandDispatcher(command);
        }
    }

    /**
     * Get the colors that can be used for a given text step's stroke.
     * @param stepData The text step to get colors for.
     * @returns A list of color objects containing fill, stroke and variant if available.
     */
    public availableStrokeColors(stepData: Step<TextStepData>): ColorOption[] {
        // Check cache first.
        const cached = this.cachedStrokeColors.get(stepData.stepName);
        if (cached) {
            return cached;
        }

        // Otherwise check option
        const colorOption = stepData.data.strokeOption;
        if (colorOption) {
            const colors =
                colorOption.variants?.map((variant: VariantResource) => {
                    return {
                        fill: variant.color,
                        stroke: variant.color,
                        variant,
                    };
                }) || [];
            this.cachedStrokeColors.set(stepData.stepName, colors);
            return colors;
        }

        return [];
    }

    public async changeStrokeColor(
        stepData: Step<TextStepData>,
        newColor: ColorOption | undefined,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
    ) {
        if (!stepData.data || !stepData.data.regions) {
            return;
        }
        const commandDispatcher = workflowManager.getCommandDispatcher();

        if (newColor?.variant) {
            workflowManager.updateMetadata(stepData.stepName, { strokeColor: newColor.variant.name });
        } else {
            workflowManager.updateMetadata(stepData.stepName, { strokeColor: newColor?.fill });
        }
        const colorDefinition: ColorDefinition | undefined = newColor
            ? {
                  browserValue: newColor.fill!,
                  pmsValue: newColor.pmsValue,
              }
            : undefined;
        workflowManager.updateStorage(stepData.stepName, {
            strokeColor: colorDefinition,
        });
        // Apply color changes to each element targeted by the step.
        for (const regionElement of elements) {
            const command = new TextStrokeCommand(regionElement.id, colorDefinition, stepData.data?.strokeThickness);
            commandDispatcher(command);
        }
    }

    /**
     * Given an element and a string, filters any characters from the string that are
     * not supported by the font of the given element.
     * @param text The text string to filter.
     * @param fontData An optional fontData object, when provided, we use the font table to determine glyphs that aren't provided and additionally strip these out also.
     * @returns A new string representing the passed string with unsupported characters removed.
     */
    public filterUnsupportedCharacters = (text: string, fontData?: FontData): string => {
        // A list of character code ranges that we don't support. Mostly known emoji ranges.
        let filteredText = text.replace(
            /(\r\n|\r|(\u2665|\u00a9|\u00ae|[\u2000-\u2017]|[\u201E-\u3300]]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]))/gm,
            "",
        );
        // Use opentype to determine if the font supports the character, remove otherwise.
        // There are special cases however. Refer to 'allowableGlyphs'
        const badCharacterCodes: string[] = [];
        const opentypeFont = fontData ? getFontMetrics(fontData.assetUrl) : undefined;
        if (opentypeFont) {
            const searchableGlyphs = filteredText
                .split("")
                .filter((char) => !allowableGlyphs.includes(char))
                .join("");
            const glyphs = searchableGlyphs.split("").map((char) => opentypeFont.getFont().charToGlyph(char));
            // Find all characters in the input text that don't work with our system.
            for (let i = 0; i < glyphs.length; i++) {
                const undefinedGlyph = glyphs[i].name === ".notdef";
                if (undefinedGlyph) {
                    badCharacterCodes.push(String.fromCharCode(searchableGlyphs.charCodeAt(i)));
                }
            }
        }
        // Once we have a list of unsupported character codes, let's remove them.
        for (let i = 0; i < badCharacterCodes.length; i++) {
            filteredText = filteredText.replaceAll(badCharacterCodes[i], "");
        }
        return filteredText;
    };

    public getProcessedInput(input: string, stepData: TextStepData, customiseAllText: boolean): string {
        const injectedText = customiseAllText ? input : this.injectReplaceableText(input, stepData);
        return applyTextTransformations(injectedText, {
            vertical: stepData.vertical,
            uppercase: stepData.uppercase,
        });
    }

    public updateInputText(
        input: string,
        elements: TextboxElement[],
        step: Step<TextStepData>,
        workflowManager: WorkflowManager,
    ): TextUpdateResult {
        const storage = workflowManager.getStepStorage(step.stepName) as TextStepStorage | undefined;
        const renderedText = renderTextTemplateForWorkflow(input, workflowManager);
        const processedText = this.getProcessedInput(renderedText, step.data, storage?.customiseAllText ?? false);

        const response: TextUpdateResult = {
            command: undefined,
            helperData: {},
            errorData: this.getErrorsForText(input, processedText, step, workflowManager),
        };

        // Check the input text for validity.
        if (response.errorData) {
            return response;
        }

        // Otherwise set helper text to character limit
        response.helperData.charactersRemaining = step.data.maxLength - processedText.length;

        // Calculate font size and lines with new text.
        // TODO: We need to shift this into TextChangeCommand
        const commands: CanvasCommand[] = [];
        const correctFontSizes = new Map<string, number>();
        const correctLineLists = new Map<string, null | string[]>();
        for (const el of elements) {
            if (!el.fontData) throw new UnhandledBehaviorError("Failed to resolve font data for text.");
            const [newFontSize, newLines] = determineCorrectFontSizeAndLines(
                el.fontSize,
                el.fontData,
                { left: el.x, top: el.y, width: el.width, height: el.height, rotation: el.rotation, panelId: "" },
                [processedText],
                { size: step.data.size, minSize: step.data.minSize, maxSize: step.data.maxSize },
            );
            correctFontSizes.set(el.id, newFontSize);
            correctLineLists.set(el.id, newLines);
            const newText = step.data.curved ? processedText : (newLines || []).join("\n");
            commands.push(this.generateTextChangeCommandsForRegion(newFontSize, step.data, el.id, newText));
        }

        if (!step.data.curved && elements.length > 0) {
            const allFit = Array.from(correctLineLists.values()).every((x) => x);
            if (!allFit) {
                if (!response.errorData) {
                    response.errorData = {};
                }
                response.errorData.doesNotFit = true;
                return response;
            }
        }

        // Notify workflow of text changes.
        workflowManager.updateStorage(step.stepName, { text: input });
        workflowManager.updateMetadata(step.stepName, {
            text: processedText,
        });

        if ((storage?.defaultCleared || !step.data.deleteDefaultOnFocus) && processedText.trim() !== "") {
            workflowManager.setMandatoryFulfilled(step.stepName, true);
        }

        response.command = new GroupCommand(commands);
        return response;
    }

    public async selectVariant(
        step: Step<TextStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setError: (status: boolean) => void,
        setHelperText: (text: string) => void,
    ) {
        const result = await this.selectVariantCommand(
            step,
            variant,
            workflowManager.getStepStorage(step.stepName) || {},
            workflowManager.getRegionElements(step.stepName),
            workflowManager,
            setError,
            setHelperText,
        );
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    public textAlign = (stepData: TextStepData) => {
        if (stepData.vertical) {
            return "center";
        }
        return stepData.textAlign || "center";
    };

    public createTextFillSpotColor(
        colorOption: OptionResource,
        variant: VariantResource,
    ): TextFillSpotColor | undefined {
        const colorProfileAsset = colorOption.colorProfile;
        if (colorProfileAsset) {
            const profileNameStrWithoutSpaces = (colorProfileAsset.name || "").replace(/\s/g, "-");
            const amountToSlice = profileNameStrWithoutSpaces.lastIndexOf("/");
            const profileNameFiltered = profileNameStrWithoutSpaces.slice(amountToSlice + 1); // Removes all characters before the last instance of /
            const finalFilteredName = profileNameFiltered.slice(0, -4); // Removes last 4 characters in the string which is the file extension (eg .icc)
            if (!variant.namedColor) throw new Error("variant named color not set!");
            return {
                profileName: finalFilteredName,
                namedColor: variant.namedColor,
            };
        }

        return undefined;
    }

    private async reload(stepData: Step<TextStepData>, workflowManager: WorkflowManager, reducerState: LayoutsState) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const option = stepData.option;

        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (option && variantId) {
                const variant = option.variants?.find((variant: VariantResource) => variant.id === variantId);
                if (variant) {
                    const fontData = await this.fontDataFromVariant(variant);
                    const regionElements = elementsForStep.map((el) => ({ id: el.id, region: el.stepRegion }));
                    const color = serializedStep.storage?.color;
                    let text = serializedStep.storage?.text;

                    // When a bundle is present we load the text aspect.
                    const bundle = workflowManager.getWorkflowExperience().getBundle();
                    if (bundle && stepData.globalPropertyAspectConfigurations) {
                        const aspects = bundle.getGlobalPropertyConfiguration()?.aspects;
                        const aspect = aspects?.find(
                            (a) =>
                                a.type === AspectType.Text &&
                                stepData.globalPropertyAspectConfigurations?.map((c) => c.aspectName).includes(a.name),
                        );
                        if (aspect) {
                            const stateManager = bundle.getGlobalPropertyStateManager();
                            const aspectText = stateManager.getAspect(aspect.name);
                            if (aspectText) {
                                text = aspectText;
                            }
                        }
                    }

                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [variant],
                        regionElements,
                        async () => {
                            workflowManager.updateMetadata(stepData.stepName, { color, text });
                            workflowManager.updateStorage(stepData.stepName, { text, inputText: text });
                            const fontSourceCommands = elementsForStep.map((el) => {
                                const command = new FontSourceCommand(el.id, fontData);
                                return command;
                            });
                            const groupCommand = new GroupCommand(fontSourceCommands);
                            workflowManager.getCommandDispatcher()(groupCommand);
                        },
                    );
                    if (elementsForStep.length === 0 && option) {
                        // Assume we are reloading a conditional step.
                        const defaultVariant = optionService.getDefaultVariant(option);
                        if (defaultVariant) {
                            const com = await this.selectVariantCommand(
                                stepData,
                                defaultVariant,
                                { text },
                                [],
                                workflowManager,
                                () => {},
                                () => {},
                            );
                            workflowManager.getCommandDispatcher()(com!.command!);
                        }
                    } else {
                        const { command } = textStepService.updateInputText(
                            text || "",
                            elementsForStep as TextboxElement[],
                            stepData,
                            workflowManager,
                        );
                        command && workflowManager.getCommandDispatcher()(command);
                    }
                }
            }
        } else {
            // When a bundle is present we load the text aspect.
            const bundle = workflowManager.getWorkflowExperience().getBundle();
            if (bundle && stepData.globalPropertyAspectConfigurations) {
                const aspects = bundle.getGlobalPropertyConfiguration()?.aspects;
                const aspect = aspects?.find(
                    (a) =>
                        a.type === AspectType.Text &&
                        stepData.globalPropertyAspectConfigurations?.map((c) => c.aspectName).includes(a.name),
                );
                if (aspect) {
                    const stateManager = bundle.getGlobalPropertyStateManager();
                    const aspectText = stateManager.getAspect(aspect.name);
                    if (aspectText) {
                        if (elementsForStep.length === 0 && option) {
                            // Assume we are reloading a conditional step.
                            const defaultVariant = optionService.getDefaultVariant(option);
                            if (defaultVariant) {
                                const com = await this.selectVariantCommand(
                                    stepData,
                                    defaultVariant,
                                    { text: aspectText },
                                    [],
                                    workflowManager,
                                    () => {},
                                    () => {},
                                );
                                workflowManager.getCommandDispatcher()(com!.command!);
                            }
                        } else {
                            const { command } = textStepService.updateInputText(
                                aspectText,
                                elementsForStep as TextboxElement[],
                                stepData,
                                workflowManager,
                            );
                            command && workflowManager.getCommandDispatcher()(command);
                        }
                    }
                }
            }
        }
    }

    private getDefaultColorVariant(step: TextStepData): VariantResource | undefined {
        const colorOption = step.colorOption;
        if (!colorOption) return undefined;
        return optionService.getDefaultVariant(colorOption);
    }

    private getDefaultColor(step: TextStepData): string | undefined {
        const colorOption = step.colorOption;
        if (!colorOption) return undefined;
        const defaultVariant = optionService.getDefaultVariant(colorOption);
        return defaultVariant?.color;
    }

    private getDefaultImageFillVariant(step: TextStepData): VariantResource | undefined {
        const option = step.imageFillOption;
        if (!option) return undefined;
        return optionService.getDefaultVariant(option);
    }

    private getDefaultStrokeColorVariant(step: TextStepData): VariantResource | undefined {
        const option = step.strokeOption;
        if (!option) return undefined;
        return optionService.getDefaultVariant(option);
    }

    /**
     * Validates a string of text based on step configuration.
     * @param text The text to validate
     * @param renderedText The text after being rendered via templating
     * @param step The step providing configuration
     * @param workflowManager Workflow manager for access to workflow state
     * @returns A list of errors. Empty if the text passes validation.
     */
    private getErrorsForText = (
        text: string,
        renderedText: string,
        step: Step<TextStepData>,
        workflowManager: WorkflowManager,
    ): undefined | TextErrorData => {
        let errorData: undefined | TextErrorData = undefined;

        // Ensure character limit is maintained
        if (!!step.data && !!step.data.maxLength && renderedText.length > step.data.maxLength) {
            if (!errorData) {
                errorData = {};
            }
            errorData.hitCharacterLimit = true;
        }

        // Check text against our blacklist
        const profanityFilter = workflowManager.getWorkflowExperience().getProfanityList();
        const words = split(text.toLowerCase());
        for (const word of words) {
            for (const i in profanityFilter) {
                const profanity = profanityFilter[i].toLowerCase().replace(/\s/g, "");
                const match = word === profanity;
                if (match) {
                    if (!errorData) {
                        errorData = {};
                    }
                    errorData.blockedProfanity = true;
                    break;
                }
            }
        }

        // Potentially don't allow newlines.
        const breaksNewlineRule = !step.data.allowNewlines && (text.includes("\n") || text.includes("\r"));
        if (breaksNewlineRule) {
            if (!errorData) {
                errorData = {};
            }
            errorData.illegalMultipleLines = true;
        }

        return errorData;
    };

    private async fontDataFromVariant(variant: VariantResource): Promise<FontData> {
        const fontAsset = variant.asset;
        if (!fontAsset) {
            throw new AssetNotFoundError(variant);
        }
        const fontUrl = fontAsset.fileLink;
        if (!fontUrl) {
            throw new ResourceNotFoundError(fontAsset);
        }
        const font = await loadFont(fontUrl);
        return {
            assetUrl: fontUrl,
            name: font.names.fullName["en"],
        };
    }

    private async selectVariantCommand(
        step: Step<TextStepData>,
        variant: VariantResource,
        storage: TextStepStorage,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
        setError: (status: boolean) => void,
        setHelperText: (text: string) => void,
        imageFillData?: { src: string; height: number; width: number; scale: number },
        strokeData?: ColorDefinition,
    ): Promise<CommandWithFollowup | null> {
        const updateId = workflowManager.markUpdatePending();

        const fontData = await this.fontDataFromVariant(variant);

        if (elements.length > 0) {
            const commands: CanvasCommand[] = elements.map((elem) => {
                const command = new FontSourceCommand(elem.id, fontData);
                return command;
            });

            if (imageFillData) {
                const imageFillCommands = elements.map((elem) => new FontImageFillCommand(elem.id, imageFillData));
                commands.push(...imageFillCommands);
            }

            if (strokeData) {
                const strokeCommands = elements.map(
                    (elem) => new TextStrokeCommand(elem.id, strokeData, step.data.strokeThickness),
                );
                commands.push(...strokeCommands);
            }

            const sizingCommand = await this.changeInputTextWithRegion(
                step,
                step.data.size || defaultFontSize,
                fontData,
                storage.text || "",
                storage,
                workflowManager,
                storage?.customiseAllText ?? false,
                setError,
                setHelperText,
            );
            sizingCommand && commands.push(sizingCommand);
            const command = new GroupCommand(commands);
            return {
                command: command,
                followup: async () => {
                    workflowManager.markUpdateCompleted(updateId);
                    await workflowManager.setSelectionsAndElements(step.stepName, [variant], elements);
                },
            };
        } else {
            const newlyCreatedData = await this.createTextboxRegions(
                step.stepName,
                variant,
                step.data,
                fontData,
                storage,
                workflowManager,
            );

            const sizingCommand = await this.changeInputTextWithRegion(
                step,
                step.data.size || defaultFontSize,
                fontData,
                newlyCreatedData[0]?.newElement.input || storage.text || step.data.defaultText || "",
                storage,
                workflowManager,
                storage?.customiseAllText ?? false,
                setError,
                setHelperText,
            );

            const commands = newlyCreatedData.flatMap((data) => data.commands);
            if (imageFillData) {
                const imageFillCommands = newlyCreatedData.map(
                    (data) => new FontImageFillCommand(data.regionElement.id, imageFillData),
                );
                commands.push(...imageFillCommands);
            }
            if (strokeData) {
                const strokeCommands = elements.map(
                    (elem) => new TextStrokeCommand(elem.id, strokeData, step.data.strokeThickness),
                );
                commands.push(...strokeCommands);
            }

            sizingCommand && commands.push(sizingCommand);
            const groupCommand = new GroupCommand(commands);

            return {
                command: groupCommand,
                followup: async () => {
                    workflowManager.markUpdateCompleted(updateId);
                },
            };
        }
    }

    /**
     * When a text step specifies replaceable text, the text input by the user will replace
     * a specific token within a larger string of text specified in the step configuration. Otherwise
     * this function will just return the text it's given when the feature is disabled.
     */
    private injectReplaceableText(text: string, stepData: TextStepData) {
        if (stepData.replaceableText) {
            return stepData.replaceableText.replace(`{{}}`, text);
        } else {
            return text;
        }
    }

    private async createTextboxRegions(
        stepName: string,
        variant: VariantResource,
        stepData: TextStepData,
        fontData: FontData,
        storage: TextStepStorage,
        workflowManager: WorkflowManager,
    ): Promise<CreatedElementData[]> {
        if (!stepData || !stepData.regions) {
            throw new Error("Step data not supplied");
        }

        const currentText = storage.text || stepData.defaultText || "";
        const renderedText = renderTextTemplateForWorkflow(currentText, workflowManager);
        const transformedText = this.getProcessedInput(renderedText, stepData, false);
        const addFontToRegion = async (region: Region): Promise<CreatedElementData> => {
            const layouts = workflowManager.getLayouts();
            const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);

            // Create new element.
            const elementId = generate();
            try {
                if (!layout) {
                    throw new RegionLayoutMissingError("Failed to find layout for region: " + region.panelId);
                }
                const colorOption = stepData.colorOption;

                let textFillSpotColor: TextFillSpotColor | undefined = undefined;
                if (colorOption && colorOption.variants) {
                    const defaultVariant =
                        colorOption.variants.find((v) => v.id === colorOption.defaultVariant?.id) ||
                        colorOption.variants[0];
                    textFillSpotColor = this.createTextFillSpotColor(colorOption, defaultVariant);
                    workflowManager.updateStorage(stepName, {
                        colorProfileAssetKey: colorOption.colorProfile?.key,
                    });
                }

                const defaultColor = await this.getDefaultColor(stepData);
                const startingFill = defaultColor ? defaultColor : "#000000";

                const newElement: TextboxElement = {
                    stepRegion: region,
                    stepName: stepName,
                    align: this.textAlign(stepData),
                    fill: storage.color ? storage.color : startingFill,
                    fontSize: stepData.size || defaultFontSize,
                    fontData,
                    id: elementId,
                    layer: region.layer,
                    layerIndex: region.layerIndex,
                    rotation: region.rotation,
                    text: transformedText,
                    input: currentText,
                    type: LayoutElementType.Textbox,
                    vertical: stepData.vertical,
                    x: region.left,
                    y: region.top,
                    height: region.height,
                    width: region.width,
                    immutable: region.immutable,
                    verticalAlign: stepData.verticalAlign || "middle",
                    curved: stepData.curved,
                    paths: stepData.paths,
                    fillSpotColorDefinition: textFillSpotColor,
                };

                const sizingCommands: CanvasCommand[] = [];
                const correctFontSizes = new Map<string, number>();
                const correctLineLists = new Map<string, null | string[]>();
                if (!newElement.fontData) throw new UnhandledBehaviorError("Failed to resolve font data for text.");
                const [newFontSize, newLines] = determineCorrectFontSizeAndLines(
                    newElement.fontSize,
                    newElement.fontData,
                    {
                        left: newElement.x,
                        top: newElement.y,
                        width: newElement.width,
                        height: newElement.height,
                        rotation: newElement.rotation,
                        panelId: "",
                    },
                    [transformedText],
                    { size: stepData.size, minSize: stepData.minSize, maxSize: stepData.maxSize },
                );
                correctFontSizes.set(newElement.id, newFontSize);
                correctLineLists.set(newElement.id, newLines);
                const newText = stepData.curved || stepData.vertical ? transformedText : (newLines || []).join("\n");
                sizingCommands.push(
                    this.generateTextChangeCommandsForRegion(newFontSize, stepData, newElement.id, newText),
                );

                const newCommand = new CreateElementCommand(newElement, layout);

                return {
                    regionElement: { id: elementId, region },
                    commands: [newCommand, ...sizingCommands],
                    newElement,
                    fontData,
                };
            } catch (err) {
                console.log(err);
                throw new FontLoadError("Error adding font to region");
            }
        };

        const newElementData = await Promise.all(stepData.regions.map(addFontToRegion)).catch((e) => {
            if (e instanceof FontLoadError) {
                toast.setLatestToast("Failed to load font.", InformationMessageType.Error);
                throw e;
            } else if (e instanceof RegionLayoutMissingError) {
                throw e;
            } else {
                throw new Error(e);
            }
        });

        await workflowManager.setSelectionsAndElements(
            stepName,
            [variant],
            newElementData.map((e) => e.regionElement),
            async () => {
                workflowManager.updateMetadata(stepName, {
                    text: currentText,
                });
                workflowManager.updateStorage(stepName, {
                    text: currentText,
                });
            },
        );

        return newElementData;
    }

    private generateTextChangeCommandsForRegion(
        correctFontSize: number,
        stepData: TextStepData,
        elementId: string,
        newText: string,
    ): GroupCommand {
        const commands: CanvasCommand[] = [];
        const textChangeCommand = new TextChangeCommand(elementId, newText);
        commands.push(textChangeCommand);
        // If the user has specified a font size range as opposed to a
        // static font size then do sizing.
        if (!stepData.size) {
            const fontSizeCommand = new FontSizeCommand(elementId, correctFontSize);
            commands.push(fontSizeCommand);
        }
        const groupCommand = new GroupCommand(commands);
        return groupCommand;
    }

    /**
     * @deprecated
     */
    private async changeInputTextWithRegion(
        step: Step<TextStepData>,
        /**
         * @deprecated
         */
        curFontSize: number,
        /**
         * @deprecated
         */
        curFontData: FontData,
        input: string | undefined,
        storage: TextStepStorage,
        workflowManager: WorkflowManager,
        customiseAllText: boolean,
        setError: (status: boolean) => void,
        setHelperText: (text: string) => void,
        setFlashRedError?: (status: boolean) => void,
    ): Promise<GroupCommand | undefined> {
        // Control characters are not valid in XML, we strip them from any input to
        // ensure that invalid data doesn't enter the SVG generated by our system.
        // \u000A and \u000D are carriage return and newline sequences, we want to keep these.
        // eslint-disable-next-line no-control-regex
        const newText = (input || "").replace(/^(?![\u000A\u000D])[\u0000-\u001F\u007F-\u009F]/g, "");
        const renderedText = renderTextTemplateForWorkflow(newText, workflowManager);
        const transformedText = this.getProcessedInput(renderedText, step.data, customiseAllText);

        const existingRegionEls = workflowManager.getRegionElements(step.stepName);
        const correctFontSizes = new Map<string, number>();
        const correctLineLists = new Map<string, null | string[]>();

        // Calculate font size and lines with new text.
        for (const regionElement of existingRegionEls) {
            if (regionElement.region) {
                const [newFontSize, newLines] = determineCorrectFontSizeAndLines(
                    curFontSize,
                    curFontData,
                    regionElement.region,
                    [transformedText],
                    { size: step.data.size, minSize: step.data.minSize, maxSize: step.data.maxSize },
                );
                correctFontSizes.set(regionElement.id, newFontSize);
                correctLineLists.set(regionElement.id, newLines);
            }
        }

        const helperTextGenerator = () => {
            // Ensure character limit is maintained
            if (!!step.data && !!step.data.maxLength && transformedText.length > step.data.maxLength) {
                if (setFlashRedError) {
                    setFlashRedError(true);
                }
                return { info: "0" };
            }

            // Check text against our blacklist
            const profanityFilter = workflowManager.getWorkflowExperience().getProfanityList();
            const words = split(transformedText.toLowerCase());
            for (const word of words) {
                for (const i in profanityFilter) {
                    const profanity = profanityFilter[i].toLowerCase().replace(/\s/g, "");
                    const match = word === profanity;
                    if (match) {
                        setError(true);
                        return { error: "Blocked profanity." };
                    }
                }
            }

            // Potentially don't allow newlines.
            if (
                !step.data.vertical &&
                !step.data.allowNewlines &&
                (transformedText.includes("\n") || transformedText.includes("\r"))
            ) {
                setError(true);
                return { error: "Cannot span multiple lines." };
            }

            if (!step.data.curved) {
                const allFit = Array.from(correctLineLists.values()).every((x) => x);
                if (!allFit) {
                    setError(true);
                    return { error: "Does not fit." };
                }
            }

            setError(false);
            return { info: (step.data.maxLength - transformedText.length).toString() };
        };

        // Check the input text for validity.
        const newHelperText = helperTextGenerator();

        if (newHelperText.error) {
            setHelperText(newHelperText.error);
            return;
        }

        setHelperText(`${newHelperText.info} characters remaining` || "");

        if (setFlashRedError) {
            return;
        }

        workflowManager.updateStorage(step.stepName, { text: newText });
        workflowManager.updateMetadata(step.stepName, {
            text: this.injectReplaceableText(newText, step.data),
        });

        if ((storage.defaultCleared || !step.data.deleteDefaultOnFocus) && newText.trim() !== "") {
            workflowManager.setMandatoryFulfilled(step.stepName, true);
        }
        const commands: CanvasCommand[] = [];
        for (const regionElement of existingRegionEls) {
            const newText = step.data.curved
                ? transformedText
                : (correctLineLists.get(regionElement.id) || []).join("\n");
            commands.push(
                this.generateTextChangeCommandsForRegion(
                    correctFontSizes.get(regionElement.id) || 1,
                    step.data,
                    regionElement.id,
                    newText,
                ),
            );
        }

        const groupCommand = new GroupCommand(commands);
        return groupCommand;
    }
}

export const textStepService = new TextStepService();
